import { Component, inject, ChangeDetectionStrategy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { DatetimePickerComponent } from '../../common/datetime-picker/datetime-picker.component'
import { resolveLocalTimeZone } from '../../utils'

export type UndoSoftDeletionDialogData = {
  callId: number
  currentRetentionPeriodStartDate: Date | null
  /** When the meeting is due for deletion, so a date that has already passed can be caught. */
  currentDeletionDate: Date | null
}

export type UndoSoftDeletionDialogResult = {
  retentionPeriodStartDate: Date | null
}

@Component({
  selector: 'app-undo-soft-deletion-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatCheckboxModule,
    TranslatePipe,
    TranslateDirective,
    MatDialogTitle,
    DatetimePickerComponent,
  ],
  templateUrl: './undo-soft-deletion-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './undo-soft-deletion-dialog.component.scss',
})
export class UndoSoftDeletionDialogComponent {
  data: UndoSoftDeletionDialogData = inject(MAT_DIALOG_DATA)

  // The picker reads and writes a plain Date, so the date is in the user's own time zone. Naming it
  // keeps it from being read as the British time the meeting screens show elsewhere.
  localTimeZone = resolveLocalTimeZone()

  private currentRetentionPeriodStartDate = this.data.currentRetentionPeriodStartDate === null ? null : new Date(this.data.currentRetentionPeriodStartDate)

  // Restoring a meeting whose deletion date has already passed would drop it straight back into the
  // deletion sweep, so the retention period has to be restarted as part of the same action rather
  // than being offered as an extra.
  mustSetRetentionPeriodStartDate = this.data.currentDeletionDate !== null && new Date(this.data.currentDeletionDate).getTime() <= Date.now()

  setRetentionPeriodStartDate = this.mustSetRetentionPeriodStartDate

  // Defaulted to now when it is being forced, so the meeting gets a full retention period back; the
  // date it had is in the past and would leave it due for deletion again.
  retentionPeriodStartDate: Date = this.mustSetRetentionPeriodStartDate ? new Date() : (this.currentRetentionPeriodStartDate ?? new Date())

  // Only a later start date pushes the deletion date out; an earlier one pulls it in, which is the
  // opposite of what a restore is for.
  get isRetentionPeriodStartDateValid(): boolean {
    if (!this.setRetentionPeriodStartDate) {
      return !this.mustSetRetentionPeriodStartDate
    }
    return this.currentRetentionPeriodStartDate === null || this.retentionPeriodStartDate > this.currentRetentionPeriodStartDate
  }

  get dialogResult(): UndoSoftDeletionDialogResult {
    return {
      retentionPeriodStartDate: this.setRetentionPeriodStartDate ? this.retentionPeriodStartDate : null,
    }
  }
}
